import React, { useEffect, useState, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { snowflakeService } from '../services/snowflakeService';

interface ReadinessGateProps {
  clientId?: string;
  workflowId?: string;
  pollIntervalMs?: number;
  onReady: () => void;
}

const ReadinessGate: React.FC<ReadinessGateProps> = ({ clientId, workflowId, pollIntervalMs = 5000, onReady }) => {
  const [checking, setChecking] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const gateStartRef = useRef<number | null>(null);
  const intervalRef = useRef<any>(null);
  const doneRef = useRef<boolean>(false);

  useEffect(() => {
    let mounted = true;
    doneRef.current = false;
    if (gateStartRef.current === null) gateStartRef.current = Date.now();

    // Helper: how many ms since gate started
    const elapsedMs = () => (gateStartRef.current ? Date.now() - gateStartRef.current : 0);

    // Helper: fetch latest module creation time in minutes
    type ModuleAge = { minutes: number | null; noModules: boolean };
    const getLatestModuleAge = async (): Promise<ModuleAge> => {
      try {
        // Prefer BFF job-monitoring endpoint (works in this project)
        // GET /api/v1/workflows/{workflow_id}/module-status
        const res = await snowflakeService.getWorkflowModuleStatus(String(workflowId));
        if (!res.success || !res.data?.modules) return { minutes: null, noModules: false };
        const modules = res.data.modules as any[];
        if (!modules || modules.length === 0) return { minutes: null, noModules: true };
        const times: number[] = [];
        modules.forEach((m) => {
          ['created_at', 'completed_at', 'started_at', 'updated_at'].forEach((k) => {
            const v = (m as any)[k];
            if (v) times.push(new Date(v).getTime());
          });
        });
        if (times.length === 0) return { minutes: null, noModules: false };
        const last = Math.max(...times);
        return { minutes: (Date.now() - last) / 60000, noModules: false };
      } catch {
        return { minutes: null, noModules: false };
      }
    };

    const check = async () => {
      if (doneRef.current) return;
      if (!clientId || !workflowId) {
        // No context; allow progression
        if (mounted) {
          doneRef.current = true;
          if (intervalRef.current) clearInterval(intervalRef.current);
          setChecking(false);
          onReady();
        }
        return;
      }
      try {
        const res = await snowflakeService.checkHarmonizedReadiness(clientId, workflowId);
        if (!mounted) return;
        if (res.success && res.data?.ready) {
          doneRef.current = true;
          if (intervalRef.current) clearInterval(intervalRef.current);
          setChecking(false);
          onReady();
        } else {
          // Apply module-time rules
          const age = await getLatestModuleAge();
          const fiveMinutes = 5;
          const elapsed = elapsedMs();
          const elapsedMin = elapsed / 60000;

          // If there are no modules at all and no data, show no data immediately
          if (age.noModules) {
            doneRef.current = true;
            if (intervalRef.current) clearInterval(intervalRef.current);
            setChecking(false);
            setMessage('No data found. Please check later.');
            return;
          }
          if (age.minutes !== null && age.minutes > fiveMinutes) {
            // Last module older than 5 minutes → stop polling, show no-data
            doneRef.current = true;
            if (intervalRef.current) clearInterval(intervalRef.current);
            setChecking(false);
            setMessage('No data found. Please check later.');
            return;
          }
          if (elapsedMin >= fiveMinutes) {
            // Gate has been polling for 5 minutes → stop and show no-data
            doneRef.current = true;
            if (intervalRef.current) clearInterval(intervalRef.current);
            setChecking(false);
            setMessage('No data found. Please check later.');
            return;
          }
          // Otherwise, keep polling
          setChecking(true);
          setError(res.error || 'Preparing data...');
        }
      } catch (e: any) {
        if (mounted) {
          setChecking(true);
          setError(e?.message || 'Preparing data...');
        }
      }
    };

    // immediate check then poll
    check();
    intervalRef.current = setInterval(check, pollIntervalMs);

    return () => {
      mounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [clientId, workflowId, pollIntervalMs, onReady]);

  // Full-page-style loading for the gated area (opaque background)
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        backgroundColor: '#ffffff'
      }}
    >
      {checking ? (
        <>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Please wait while we prepare your data
          </Typography>
          {error ? (
            <Typography variant="body2" color="text.secondary">
              {error}
            </Typography>
          ) : null}
        </>
      ) : (
        message ? (
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {message}
          </Typography>
        ) : null
      )}
    </Box>
  );
};

export default ReadinessGate;


